<template>
  <v-layout wrap>
    <v-flex xs12 pb-0>
      <v-layout wrap align-center>
        <v-flex class="d-flex align-center justify-space-between" xs12 mt-3>
          <h1 class="headline">{{ $t('investorList.title') }}</h1>

          <div>
            <v-btn
              elevation="0"
              class="mr-2"
              color="blue-grey white--text"
              :to="{ path: 'statistics' }"
            >
              {{ $t('workersStatistics.title') }}
            </v-btn>

            <export-button
              class="mr-2"
              action-name="investor-board/downloadReport"
              :file-name="$t('investorList.title')"
              :disabled="dataLoading"
              :query="{ ...filters, dates }"
            />

            <v-btn
              color="info"
              elevation="0"
              :disabled="paymentTypesLoading"
              @click="historyDialog = true"
            >
              {{ $t('investor-report.history') }}
            </v-btn>
          </div>
        </v-flex>
      </v-layout>

      <v-layout wrap>
        <v-flex xs12 sm12 mt-4>
          <v-layout wrap align-center>
            <v-flex xs12 sm12 mt-2>
              <v-layout wrap align-start class="search-wrapper">
                <div class="d-flex search">
                  <v-text-field
                    ref="search"
                    v-model="searchString"
                    :placeholder="$t('rentsList.search.placeholder')"
                    autocomplete="off"
                    solo
                  />
                  <v-btn
                    type="button"
                    class="search-btn warning"
                    depressed
                    large
                    @click="filters.search = searchString"
                  >
                    {{ $t('common.btns.search') }}
                  </v-btn>
                </div>

                <v-alert
                  class="ml-auto mb-0"
                  :color="accountColor"
                  text
                  outlined
                  dense
                >
                  <div class="d-flex align-items-center">
                    <v-icon :color="accountColor">mdi-account-cash</v-icon>
                    <b class="ml-1 mr-2"
                      >{{ $t('investor-report.account') }}:</b
                    >
                    <b>{{ balance }}</b>
                  </div>
                </v-alert>
                <v-alert
                  class="ml-2"
                  :color="payment ? 'warning' : 'grey'"
                  text
                  outlined
                  dense
                >
                  <b> {{ $t('investor-report.pay') }}: {{ payment }} </b>
                </v-alert>
                <v-alert
                  class="ml-2"
                  :color="deposit ? 'success' : 'grey'"
                  text
                  outlined
                  dense
                >
                  <b> {{ $t('common.table.deposit') }}: {{ deposit }} </b>
                </v-alert>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
    </v-flex>

    <div class="investor-board__filters">
      <v-menu
        ref="menuDate"
        :close-on-content-click="false"
        v-model="menuDate"
        :return-value.sync="dates"
        max-width="280px"
        min-width="280px"
        class="pr-4 mt-2"
        transition="scale-transition"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            v-on="on"
            v-model="dates"
            prepend-icon="mdi-calendar"
            :label="$t('common.fields.rentDate')"
            readonly
            class="date investor-board__filters-item"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="dates"
          color="primary lighten-1"
          width="280"
          range
          :locale="$i18n.locale"
          :selected-items-text="dates.toString()"
          :allowed-dates="allowedDates"
          @input="changeDate"
        ></v-date-picker>
      </v-menu>

      <v-select
        v-model="filters.status"
        :label="$t('common.fields.status')"
        item-text="name"
        item-value="id"
        class="investor-board__filters-item"
        clearable
        :items="carStatuses.filter(({ id }) => id !== '0')"
        :loading="dataLoading"
      ></v-select>

      <v-text-field
        v-model="expenseSearch"
        class="investor-board__filters-item"
        :label="$t('workersList.expenceSearch')"
      >
        <template #append>
          <div class="d-flex mt-1">
            <v-btn
              v-if="expenseSearch"
              class="mr-1"
              icon
              x-small
              @click="
                () => {
                  expenseSearch = '';
                  filters.expence_comment = '';
                }
              "
            >
              <v-icon small> mdi-close </v-icon>
            </v-btn>
            <v-btn
              color="warning"
              elevation="0"
              rounded
              x-small
              @click="filters.expence_comment = expenseSearch"
            >
              <v-icon small> mdi-magnify </v-icon>
            </v-btn>
          </div>
        </template>
      </v-text-field>
    </div>

    <v-flex xs12>
      <v-data-table
        ref="investor-board"
        class="investor-board mt-5"
        hide-default-header
        hide-default-footer
        :height="tableHeight"
        :loading="loading"
        :headers="headers"
        :items="list"
        :items-per-page="-1"
        :no-data-text="$t('common.table.noData')"
      >
        <template #header="{props}">
          <thead class="v-data-table-header">
            <tr>
              <th v-for="(header, key) in props.headers" :key="key">
                {{ $t(header.text) }}
              </th>
            </tr>
          </thead>
        </template>

        <template #body="{ items }">
          <tbody v-if="items.length > 0">
            <tr v-for="(item, key) in items" :key="key">
              <td>{{ item.num }}</td>
              <td>{{ item.created_at || '-' }}</td>
              <td>{{ item.car.number || '-' }}</td>
              <!-- Comment 
              <td>{{ (item.driver && item.driver.user.name) || '-' }}</td>
              -->
              <td>{{ item.car_status_name || '-' }}</td>
              <td>
                {{ formatAmount(item.income_total) }}
                <v-tooltip v-if="item.income_comment" top color="warning">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="ml-2"
                      color="warning"
                      small
                      v-on="on"
                      v-bind="attrs"
                    >
                      mdi-comment-text-outline
                    </v-icon>
                  </template>
                  <div v-html="item.income_comment" />
                </v-tooltip>
              </td>
              <td>{{ item.explanation || '-' }}</td>
              <td>
                {{ formatAmount(item.expense_total) }}
                <v-tooltip v-if="item.expense_comment" top color="warning">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="ml-2"
                      color="warning"
                      small
                      v-on="on"
                      v-bind="attrs"
                    >
                      mdi-comment-text-outline
                    </v-icon>
                  </template>
                  <div v-html="item.expense_comment" />
                </v-tooltip>
              </td>
              <td>
                {{ formatAmount(item.summary) }}
              </td>
            </tr>
            <tr class="investor-board__totals grey lighten-3">
              <td>
                <b>{{ $t('common.total') }}</b>
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td></td>
              <td>
                <b>{{ formatAmount(incomeTotal) }}</b>
              </td>
              <td></td>
              <td>
                <b>{{ formatAmount(expenseTotal) }}</b>
              </td>
              <td>
                <b>{{ formatAmount(remainderTotal) }}</b>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td class="text-center" colspan="7">
                {{ $t('common.table.noData') }}
              </td>
            </tr>
          </tbody>
        </template>
      </v-data-table>
    </v-flex>

    <transactions-history-dialog
      v-model="historyDialog"
      :items="history"
      no-edit
    />
  </v-layout>
</template>

<script>
import { mapState } from 'vuex';
import formatAmount from '@/helpers/formatAmount';
import TransactionsHistoryDialog from '@/components/investor-report/TransactionsHistoryDialog.vue';
import ExportButton from '@/components/shared/ExportButton.vue';
import calcTableHeight from '@/helpers/calcTableHeight';

export default {
  components: { TransactionsHistoryDialog, ExportButton },
  data: () => ({
    loading: true,
    dataLoading: false,
    paymentTypesLoading: false,
    menuDate: false,
    historyDialog: false,
    tableHeight: 'auto',
    dates: [],
    searchString: '',
    expenseSearch: '',
    filters: {
      search: '',
      status: '',
      expence_comment: '',
    },

    headers: [
      { text: 'common.number' },
      { text: 'common.fields.bookingDate' },
      { text: 'auto.fields.number' },
      //{ text: 'sidebar.drivers' },
      { text: 'common.fields.status' },
      { text: 'common.table.coming' },
      { text: 'workersList.explanation' },
      { text: 'common.table.consumption' },
      { text: 'investorList.remainder' },
    ],
  }),
  watch: {
    dates(value) {
      if (value.length === 1) return false;
      this.fetchList({ ...this.filters, dates: this.dates, page: 1 });
      return null;
    },
    filters: {
      deep: true,
      handler(data) {
        this.fetchList({ ...data, dates: this.dates, page: 1 });
      },
    },
  },
  computed: {
    ...mapState({
      list: (state) => state['investor-board'].list,
      currentPage: (state) => state['investor-board'].currentPage,
      lastPage: (state) => state['investor-board'].lastPage,
      perPage: (state) => state['investor-board'].perPage,
      carStatuses: (state) => state['workers-board'].carStatuses,
      history: (state) => state['investor-board'].history,
      balance: (state) => state['investor-board'].balance,
      payment: (state) => state['investor-board'].payment,
      deposit: (state) => state['investor-board'].deposit,
    }),
    today() {
      const date = new Date();
      const formatNum = (v) => (v < 10 ? `0${v}` : v);

      return `${date.getFullYear()}-${formatNum(
        date.getMonth() + 1,
      )}-${date.getDate()}`;
    },
    incomeTotal() {
      /* eslint-disable */
      return this.list.reduce((total, item) => {
        return (total += item.income_total);
      }, 0);
      /* eslint-enable */
    },
    expenseTotal() {
      /* eslint-disable */
      return this.list.reduce((total, item) => {
        return (total += item.expense_total);
      }, 0);
      /* eslint-enable */
    },
    remainderTotal() {
      /* eslint-disable */
      return this.list.reduce((total, item) => {
        return (total += item.summary);
      }, 0);
      /* eslint-enable */
    },
    accountColor() {
      const total = Number(this.balance);
      let color = 'success';

      if (total < 0) color = 'error';
      else if (total === 0) color = 'grey';

      return color;
    },
  },
  methods: {
    formatAmount,
    changeDate(val) {
      if (val.length === 2) {
        this.$refs.menuDate.save(this.dates);
      }
    },
    allowedDates(value) {
      const [from, to] = this.dates;

      if (!from) return true;
      if (to) return true;

      const firstDate = new Date(from);
      const nextDate = new Date(value);

      return nextDate >= firstDate;
    },
    async fetchStatuses() {
      this.dataLoading = true;
      await this.$store.dispatch('workers-board/fetchDialogProps');
      this.dataLoading = false;
    },
    async fetchList(params = {}) {
      this.loading = true;
      await this.$store.dispatch('investor-board/fetchList', params);
      this.loading = false;
    },
    async fetchPaymentTypes() {
      this.paymentTypesLoading = true;
      await this.$store.dispatch('investor-report/fetchPaymentTypes');
      this.paymentTypesLoading = false;
    },
  },
  created() {
    this.dates = [this.today, this.today];
    this.fetchStatuses();
    this.fetchPaymentTypes();
  },
  mounted() {
    const table = this.$refs['investor-board'];
    this.tableHeight = calcTableHeight(table.$el);
  },
};
</script>

<style lang="scss">
.investor-board {
  &__filters {
    display: flex;
    align-items: center;

    &-item {
      max-width: 200px;
      margin-right: 30px;
    }
  }

  thead {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  &__totals {
    position: sticky;
    bottom: 0;
  }
}

.v-pagination {
  button {
    box-shadow: none !important;
  }
}

.search-wrapper {
  .search {
    margin-right: -12px;
    margin-bottom: -12px;
    max-width: 570px;
    width: 100%;

    @media (max-width: 480px) {
      margin-right: 0px;
      margin-bottom: -12px;
      max-width: 100%;
    }
  }

  @media (max-width: 480px) {
    flex-direction: column;

    .search {
      width: 100%;
    }
  }
}
.v-date-picker-title__date {
  font-size: 18px;
}
</style>
